import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ReactComponent as IconInfoCircle } from 'bootstrap-icons/icons/info-circle.svg'
import { ReactComponent as IconClipboard } from 'bootstrap-icons/icons/clipboard2.svg'

const POPOVER_WIDTH = 400
const VIEWPORT_MARGIN = 16

// Colores del handoff de Figma (nodo 3303:439-442 "Modal/Trial/HCE", card de Juan
// Cruz/Marina Duran, 27/8/2026) — mismos valores que $handoff-accent/$handoff-muted
// de styles/variables.scss (no hay mecanismo de compartir variables SCSS con JS en
// este repo, ver Components/DrApp/DrApp.js#DRAPP_COLOR para el mismo patrón).
const ACCENT = '#ff9d88' // $handoff-accent
const MUTED = '#6b7280' // $handoff-muted
const CARD_BORDER = '#e9f3fa' // $handoff-card-border
const BG_TERTIARY = '#f8fafc' // $handoff-bg-tertiary

/**
 * Reemplaza a BetaBadge en los accesos a features "Trial" (ex-beta) de la HCE —
 * rediseño de Marina Duran, card de Juan Cruz 27/8/2026. A diferencia de BetaBadge
 * (un pill "BETA" que abría BetaFeedback directo), acá el ícono "i" abre primero un
 * popup con contexto de la feature; "Dejar reseña" adentro dispara el mismo
 * mecanismo de siempre (`onLeaveReview`, normalmente `xFeedbackRef.current
 * ?.openManual()` — ver EHR.js). El botón/label de la feature en sí NO vive en este
 * componente: sigue siendo hermano, sin cambios de comportamiento (clic ahí abre la
 * feature como siempre — ver Figma nodes 3303:264/3303:267, mismo patrón).
 *
 * Popover bespoke (no react-tooltip): el contenido tiene un botón interactivo
 * adentro ("Dejar reseña"), y react-tooltip no se usa en modo click en ningún otro
 * lugar del repo — mismo patrón ya establecido en Ranges.js/Event.js
 * (position-absolute + cierre por click afuera vía useRef+mousedown).
 *
 * Bug real (reporte de Nacho, prod, 2026-08-28 — Percentiles): el popover vivía
 * `position:absolute` DENTRO del modal que lo abre, y varios de esos modales
 * (Percentiles.js, Odontograma.js, Escriba) son `modal-dialog-scrollable`
 * (Bootstrap le pone `overflow-y:auto` a `.modal-content`) — cualquier
 * descendiente absoluto que no entre en el alto visible de ESE momento del
 * scroll queda cortado ahí, sin importar el z-index (overflow y stacking son
 * cosas distintas). Fix: portal a `document.body` + `position:fixed` con
 * coordenadas calculadas del trigger real (`getBoundingClientRect`), así el
 * popover ya no es descendiente de ningún contenedor con overflow propio.
 * `maxHeight`+`overflowY:auto` como red de seguridad final — si en una
 * pantalla muy baja igual no entra completo, se ve con scroll interno en vez
 * de cortado en silencio.
 */
export function TrialInfoPopover({ title, description, onLeaveReview, align = 'right' }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const containerRef = useRef(null)
  const popoverRef = useRef(null)

  const updateCoords = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
    let left = align === 'left' ? rect.left : rect.right - width
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), window.innerWidth - width - VIEWPORT_MARGIN)
    const top = Math.min(rect.bottom + 8, window.innerHeight - VIEWPORT_MARGIN)
    setCoords({ top, left, width })
  }

  // Layout (no efecto normal): calcula la posición ANTES del paint, para que
  // nunca se vea un frame en (0,0) mientras se abre.
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return }
    updateCoords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align])

  // El modal detrás puede seguir scrolleando con el popover abierto (scroll
  // en captura: el scroll de un contenedor anidado no burbujea como evento
  // nativo) — recalcular para que no quede desalineado del ícono que lo abrió.
  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', updateCoords)
    window.addEventListener('scroll', updateCoords, true)
    return () => {
      window.removeEventListener('resize', updateCoords)
      window.removeEventListener('scroll', updateCoords, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    const handleClickOutside = event => {
      const insideTrigger = containerRef.current?.contains(event.target)
      const insidePopover = popoverRef.current?.contains(event.target)
      if (!insideTrigger && !insidePopover) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // ESC además del click afuera — mismo criterio de accesibilidad que el resto de
  // los popovers/modales del repo (BetaFeedback no lo tiene, pero los modales
  // full-screen sí — ver Odontograma.js/Percentiles.js).
  useEffect(() => {
    if (!open) return
    const handleEscape = event => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  return (
    // position-relative + ref acá arriba (no solo en el popover): el listener de
    // "click afuera" necesita que el propio ícono trigger cuente como "adentro",
    // si no un clic para REABRIR justo después de cerrar por click-afuera contaría
    // como "afuera" y el popover jamás abriría en el mismo gesto.
    <div className="position-relative d-inline-flex" ref={containerRef}>
      <button
        type="button"
        className="btn btn-sm border-0 p-0 d-inline-flex align-items-center justify-content-center"
        style={{ color: ACCENT, lineHeight: 0 }}
        aria-label={t('trial_info_button_label', 'Más información sobre {{title}}', { title })}
        aria-expanded={open}
        // stopPropagation: este botón es HERMANO del botón que abre la feature (no
        // anidado — un <button> dentro de otro es HTML inválido, mismo motivo ya
        // documentado en EHR.js:1136-1140 para BetaBadge), así que en el DOM normal
        // no hay bubbling que lo dispare — el stopPropagation es un guardarraíl
        // explícito por si algún caller futuro los anida sin querer.
        onClick={event => { event.stopPropagation(); setOpen(o => !o) }}
        data-testid="trial-info-button"
      >
        <IconInfoCircle width={16} height={16} />
      </button>

      {open && coords && createPortal(
        <div
          ref={popoverRef}
          className="handoff-v2 shadow rounded-4 bg-white"
          // position:fixed + coords calculados del trigger real (ver
          // updateCoords arriba) — portaleado a document.body para no
          // heredar el overflow:hidden/auto de ningún ancestro (el bug real:
          // modal-dialog-scrollable le pone overflow-y:auto a .modal-content,
          // y como descendiente absoluto de ESE contenedor el popover
          // quedaba cortado ahí). maxHeight+overflowY:auto es la red de
          // seguridad final para pantallas muy bajas.
          style={{
            position: 'fixed', zIndex: 1060, top: coords.top, left: coords.left, width: coords.width,
            maxHeight: `calc(100vh - ${coords.top}px - ${VIEWPORT_MARGIN}px)`, overflowY: 'auto',
            padding: 24, border: `1px solid ${CARD_BORDER}`,
          }}
          role="dialog"
          aria-label={title}
          data-testid="trial-info-popover"
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <IconClipboard width={16} height={16} style={{ color: ACCENT, flexShrink: 0 }} aria-hidden="true" />
              <span className="font-nunito" style={{ color: ACCENT, fontWeight: 900, fontSize: 14, letterSpacing: '0.7px', textTransform: 'uppercase' }}>
                {title}
              </span>
            </div>
            <button type="button" className="btn-close" aria-label={t('close', 'Cerrar')} onClick={() => setOpen(false)} />
          </div>

          <p className="text-center mb-3" style={{ color: MUTED, fontSize: 16 }}>
            {description}
          </p>

          <div className="d-flex gap-2 rounded-4 mb-3" style={{ background: BG_TERTIARY, padding: 8 }}>
            <IconInfoCircle width={16} height={16} className="flex-shrink-0 mt-1" style={{ color: MUTED }} aria-hidden="true" />
            <div style={{ color: MUTED, fontSize: 14 }}>
              <p className="mb-1">
                {t(
                  'trial_info_disclaimer_1',
                  'Esta herramienta está en modo Trial. Podés usarla libremente pero puede cambiar o dejar de estar disponible —obviamente todo lo que ya cargaste queda guardado en la HCE.'
                )}
              </p>
              <p className="mb-0">{t('trial_info_disclaimer_2', 'Tu opinión nos ayuda a decidir si la mantenemos.')}</p>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-outline-accent w-100"
            data-testid="trial-info-leave-review"
            onClick={() => { setOpen(false); onLeaveReview?.() }}
          >
            {t('trial_info_leave_review', 'Dejar reseña')}
          </button>
        </div>,
        document.body
      )}
    </div>
  )
}

export default TrialInfoPopover
